import React, { useEffect, useState } from 'react';
import { downloadInvestorOnePagerDocx } from '../engine/capitalDocx';
import { track } from '../lib/track';
import { investorApi } from '../crm/api';

function fmtPct(x) {
  if (x == null || Number.isNaN(+x)) return '—';
  return `${(+x * 100).toFixed(1)}%`;
}

function fmtMM(x) {
  if (x == null || Number.isNaN(+x)) return '—';
  return `$${Number(x).toFixed(1)}MM`;
}

export default function InvestorPortal({ user, onLogout }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    investorApi
      .portal()
      .then((res) => {
        if (cancelled) return;
        setData(res);
        track('investor_portal_viewed', {
          firm_id: res.firm?.id,
          pack_count: res.packs?.length || 0,
        });
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load portal');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const pack = data?.packs?.[0];
  const freeze = pack?.freeze;
  const base = freeze?.results?.base || {};

  const downloadPack = async () => {
    if (!pack?.id) return;
    try {
      const full = await investorApi.pack(pack.id);
      if (!full?.freeze) throw new Error('Pack has no projection payload');
      downloadInvestorOnePagerDocx(full.freeze);
      track('investor_one_pager_downloaded', { pack_id: pack.id, firm_id: data?.firm?.id });
    } catch (err) {
      setError(
        err.message ||
          'Download failed — ask your Firebrand contact for the Word one-pager.',
      );
    }
  };

  return (
    <div className="investor-portal">
      <header className="investor-header">
        <div className="investor-header-inner">
          <div className="header-mark">FB</div>
          <div>
            <h1>Firebrand Energy</h1>
            <p>Eagle East TX · Investor portal</p>
          </div>
          <div className="investor-header-user">
            <div>{user?.name}</div>
            <div className="crm-muted">{data?.firm?.firm_name || user?.email}</div>
            <button type="button" className="header-logout" onClick={onLogout}>
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="investor-main">
        {loading && (
          <div className="loading-screen">
            <div className="loading-spinner" />
            <p>Loading your materials…</p>
          </div>
        )}
        {error && <div className="auth-error">{error}</div>}
        {!loading && data && (
          <>
            <section className="investor-hero">
              <p className="investor-kicker">Prepared for {data.firm.firm_name}</p>
              <h2>{data.thesis.headline}</h2>
              <p className="investor-lead">{data.thesis.summary}</p>
              {data.thesis.fit_rationale && (
                <blockquote className="investor-fit">“{data.thesis.fit_rationale}”</blockquote>
              )}
            </section>

            {freeze ? (
              <section className="investor-pack">
                <div className="investor-pack-head">
                  <div>
                    <h3>{pack.label || freeze.label || 'Working case'}</h3>
                    <p className="crm-muted">
                      Underwriting snapshot · hash {freeze.contentHash || pack.content_hash || '—'}
                    </p>
                  </div>
                  <button type="button" className="btn-primary" onClick={downloadPack}>
                    Download one-pager
                  </button>
                </div>
                <p className="crm-muted" style={{ marginBottom: 12 }}>
                  Illustrative asset-underwriting metrics only — not LP / investor returns.
                  Financing status:{' '}
                  {(data.thesis?.financing_status || 'none_assumed').replace(/_/g, ' ')}.
                </p>
                <div className="investor-kpi-grid">
                  <div className="investor-kpi">
                    <div className="kpi-label">Illustrative asset bid</div>
                    <div className="kpi-value">{fmtMM(freeze.assumptions?.bidPriceMM)}</div>
                  </div>
                  <div className="investor-kpi">
                    <div className="kpi-label">Illustrative asset PV</div>
                    <div className="kpi-value">{fmtMM(base.pvMM)}</div>
                  </div>
                  <div className="investor-kpi">
                    <div className="kpi-label">Illustrative asset-model IRR</div>
                    <div className="kpi-value">{fmtPct(base.irrAnnual)}</div>
                  </div>
                  <div className="investor-kpi">
                    <div className="kpi-label">Illustrative asset-model MOIC</div>
                    <div className="kpi-value">
                      {base.moic != null ? `${Number(base.moic).toFixed(2)}x` : '—'}
                    </div>
                  </div>
                  <div className="investor-kpi">
                    <div className="kpi-label">NTM CF (asset)</div>
                    <div className="kpi-value">{fmtMM(base.ntmCfMM)}</div>
                  </div>
                  <div className="investor-kpi">
                    <div className="kpi-label">HH assumption</div>
                    <div className="kpi-value">
                      {freeze.assumptions?.hh != null
                        ? `$${Number(freeze.assumptions.hh).toFixed(2)}`
                        : '—'}
                    </div>
                  </div>
                </div>
                {(freeze.results?.namedScenarios || []).length > 0 && (
                  <div className="investor-scenarios">
                    <h4>Named scenarios (asset-model)</h4>
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Case</th>
                          <th>Asset PV</th>
                          <th>Asset-model IRR</th>
                          <th>NTM CF</th>
                        </tr>
                      </thead>
                      <tbody>
                        {freeze.results.namedScenarios.map((s) => (
                          <tr key={s.id || s.label}>
                            <td>{s.label}</td>
                            <td>{fmtMM(s.pvMM)}</td>
                            <td>{fmtPct(s.irrAnnual)}</td>
                            <td>{fmtMM(s.ntmCfMM)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            ) : (
              <section className="investor-pack investor-pack-empty">
                <h3>Materials coming soon</h3>
                <p>
                  Your Firebrand contact is preparing a capital pack for {data.firm.firm_name}.
                  You’ll see KPIs and a downloadable one-pager here once it’s published.
                </p>
              </section>
            )}

            <section className="investor-next">
              <h3>Next step</h3>
              <p>
                Reply to your Firebrand outreach to schedule a diligence call, or request a full CA
                and VDR access if you want well-level detail.
              </p>
            </section>
          </>
        )}
      </main>

      <footer className="investor-footer">
        Confidential · Firebrand Energy · Illustrative asset-underwriting materials · Financing
        none assumed · Not LP returns · Not a full VDR
      </footer>
    </div>
  );
}
